import { useParams, useMatch, useSearch } from '@tanstack/react-router'
import type { Profile } from '@/lib/profile-search-schema'

export type ProfileRouteParams = {
  jobId?: string
  offerId?: string
  employeeId?: string
  teamId?: string
  groupId?: string
  applicantId?: string
}

/**
 * Pure first/second profile derivation, shared by `useRootSearch` (client render) and route
 * loaders (server prefetch). A loader can call this with the route's `search` + `params` to build
 * the EXACT same `first`/`second` the component will compute, so the prefetched `getReport` query
 * key lines up with the component's `useQuery` (a mismatch would cause a double fetch).
 */
export function deriveProfiles(
  search: { first?: Profile; second?: Profile },
  params: ProfileRouteParams,
  isReportPage: boolean,
  precedence: 'search' | 'params' = 'search',
  isMarketPage = false,
) {
  const { jobId, offerId, employeeId, teamId, groupId, applicantId } = params

  // Compute first profile if not explicitly set in search (search params take precedence)
  const first =
    (precedence === 'search' ? search.first : undefined) ??
    (isReportPage
      ? ({ type: 'applicant' } as const)
      : isMarketPage
        ? // The market page defaults to the market-wide profile so a bare `/market` URL loads a report
          // instead of an empty selection state (the breadcrumb sets the same `{ type: 'market' }`).
          ({ type: 'market' } as const)
        : jobId
          ? ({ type: 'job', id: Number(jobId) } as const)
          : offerId
            ? ({ type: 'job', id: Number(offerId) } as const)
            : employeeId
              ? ({ type: 'employee', id: Number(employeeId) } as const)
              : teamId
                ? ({ type: 'team', id: Number(teamId), evaluation: 'owns' } as const)
                : groupId
                  ? ({ type: 'org_group', id: groupId } as const)
                  : precedence === 'params'
                    ? search.first
                    : undefined)

  // Compute second profile if not explicitly set in search
  const second =
    (precedence === 'search' ? search.second : undefined) ??
    (applicantId
      ? ({ type: 'applicant', id: Number(applicantId) } as const)
      : precedence === 'params'
        ? search.second
        : undefined)

  return { first, second }
}

/**
 * Drops every report-scoped search key, so a navigation lands on the clean single-profile view instead
 * of carrying the previous profile's selection over. Spread it and override what the link does want:
 *
 * ```ts
 * search={(prev) => ({ ...clearProfileSelection(prev), q: undefined })}
 * search={(prev) => ({ ...clearProfileSelection(prev), first: { type: 'employee', id }, selected: 'first' })}
 * ```
 *
 * It exists because this rule was spelled out at ~40 links by hand, and adding `collective` /
 * `collectiveFilters` for "Sort collectives by adjust" meant editing 22 of them — while the shared ones
 * (the nav stack's pop, the report tabs, the report error screen) were missed and kept leaking a stale
 * collective onto an unrelated profile. Any future report-scoped key goes here, once.
 */
export function clearProfileSelection<T extends object>(prev: T) {
  return {
    ...prev,
    first: undefined,
    second: undefined,
    selected: undefined,
    collective: undefined,
    collectiveFilters: undefined,
  }
}

/**
 * Whether the "Ordenar colectivos por ajuste" card is the active right-hand view: a collective is
 * chosen and we're not already in a two-profile comparison. `collective` deliberately survives into
 * compare mode so "Back to list" can return to the ranked list, which is why `selected` is checked too.
 *
 * It lives here, with the rest of the search-state derivation, rather than in the card component it
 * happens to gate — `report.tsx` needs the same answer to decide whether to hide the report body, and
 * a page importing a routing predicate out of a card's module inverts the layering.
 */
export function isCollectiveActive(search: { collective?: string; selected: string }) {
  return search.collective != null && search.selected !== 'compare'
}

/**
 * Enhanced useSearch hook that includes profile inference from route params.
 *
 * This hook combines search params with route params to compute profile values.
 * By default search params take precedence over route params.
 * You can change this behavior by passing the `precedence` option.
 *
 * Usage:
 * ```tsx
 * const { first, second, type, selected } = useRootSearch()
 * ```
 *
 * The computed `first` and `second` profiles will be inferred from route params
 * (jobId, employeeId, teamId, groupId, applicantId) if not explicitly set in search.
 */
export function useRootSearch(opts: { precedence: 'search' | 'params' } = { precedence: 'search' }) {
  const search = useSearch({ from: '__root__' })
  const params: ProfileRouteParams = useParams({ strict: false })
  const isReportPage = Boolean(useMatch({ from: '/_app/report', shouldThrow: false }))
  const isMarketPage = Boolean(useMatch({ from: '/_app/market', shouldThrow: false }))
  const precedence = opts.precedence

  return { ...search, ...deriveProfiles(search, params, isReportPage, precedence, isMarketPage) }
}
